import React, {useState} from 'react';
import {
    Box, Button, Skeleton,
    Paper, Stack, Table, TableBody,
    TableCell, TableContainer,
    TableHead, TableRow, Typography, Slide
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InputField, SnackbarField} from '@/components/common';
import TablePagination from '@mui/material/TablePagination';
import {AuditFsAnnualService} from '@/services/auditService';
import {AnnualFsStatusEnum} from '@/services/auditService';
import FsAdjustment from '@/pages/components/audit/adjustment/FsAdjustment';

export default function AuditAdjustmentList() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error'
    }>({open: false});
    // pagination
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [total, setTotal] = useState<number>(0);
    // panel switching
    const [showAdjustment, setShowAdjustment] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [filterYearOfAssessment, setFilterYearOfAssessment] = useState('');
    const [expandedCompany, setExpandedCompany] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const resp = await AuditFsAnnualService.queryPage(AnnualFsStatusEnum.AUDIT_ADJUSTMENT, {
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                companyName: filterCompanyName || undefined,
                yearOfAssessment: filterYearOfAssessment || undefined,
            });
            const list = (resp?.list || []) as any[];
            setRows(list);
            setTotal(Number(resp?.totalRow || 0));
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Load failed');
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        const handler = setTimeout(() => {
            load();
        }, 400);
        return () => clearTimeout(handler);
    }, [filterCompanyName, filterYearOfAssessment]);

    React.useEffect(() => {
        load();
    }, [pageNum, pageSize]);

    return (
        <Box sx={{p: '20px', height: '100%', boxSizing: 'border-box'}}>
            <Box sx={{position: 'relative', height: 'calc(100vh - 120px)', minHeight: '70vh'}}>
                {/* List View */}
                <Slide direction="left" in={!showAdjustment} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pr: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                            <Typography variant="h6" fontWeight={700}>
                                Audit Adjustment
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <InputField size="small" label="Company Name" value={filterCompanyName} onChange={e => {
                                    setPageNum(1);
                                    setFilterCompanyName(e.target.value);
                                }}/>
                                <InputField size="small" label="Assessment Year" value={filterYearOfAssessment}
                                           placeholder="e.g. 2024/25"
                                           onChange={e => {
                                               setPageNum(1);
                                               setFilterYearOfAssessment(e.target.value);
                                           }}/>
                            </Stack>
                        </Stack>

                        {error && (
                            <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                        )}

                        <TableContainer
                            component={Paper}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                boxShadow: theme.shadows[2],
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Table
                                size="small"
                                stickyHeader
                                sx={{
                                    '& .MuiTableCell-stickyHeader': {
                                        bgcolor: 'background.paper',
                                        zIndex: 3,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 500}}>Company Name</TableCell>
                                        <TableCell sx={{fontWeight: 500}}>Company Chinese Name</TableCell>
                                        <TableCell sx={{fontWeight: 500}}>Records</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && [
                                        ...Array(pageSize).keys()
                                    ].map((i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No data</TableCell>
                                        </TableRow>
                                    )}
                                    {!loading && (() => {
                                        const grouped: Record<string, any[]> = {};
                                        rows.forEach(r => {
                                            const key = r.companyName || `unknown-${r.companyId}`;
                                            if (!grouped[key]) grouped[key] = [];
                                            grouped[key].push(r);
                                        });
                                        Object.values(grouped).forEach(list => list.sort((a, b) => {
                                            const ya = a.yearOfAssessment || '';
                                            const yb = b.yearOfAssessment || '';
                                            return yb.localeCompare(ya);
                                        }));
                                        return Object.entries(grouped).map(([companyName, annuals]) => {
                                            const isExpanded = expandedCompany === companyName;
                                            const first = annuals[0];
                                            return (
                                                <React.Fragment key={companyName}>
                                                    <TableRow hover sx={{cursor: 'pointer'}}
                                                        onClick={() => setExpandedCompany(isExpanded ? null : companyName)}>
                                                        <TableCell>{companyName}</TableCell>
                                                        <TableCell>{first?.companyChineseName || '-'}</TableCell>
                                                        <TableCell sx={{color: 'text.secondary', fontSize: 12}}>
                                                            {annuals.length} record(s)
                                                        </TableCell>
                                                    </TableRow>
                                                    {isExpanded && (
                                                        <TableRow>
                                                            <TableCell colSpan={3} sx={{bgcolor: 'background.default', p: 0}}>
                                                                <Box sx={{borderTop: `1px solid ${theme.palette.divider}`, pl: '80px'}}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell sx={{fontWeight: 600}}>Year Of Assessment</TableCell>
                                                                                <TableCell sx={{fontWeight: 600}}>Period To</TableCell>
                                                                                <TableCell sx={{fontWeight: 600}}>Workflow</TableCell>
                                                                                <TableCell sx={{fontWeight: 600}}>Action</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {annuals.map((r, idx) => (
                                                                                <TableRow key={String(r.annualId ?? idx)} hover>
                                                                                    <TableCell>{r.yearOfAssessment}</TableCell>
                                                                                    <TableCell>{r.periodTo}</TableCell>
                                                                                    <TableCell>
                                                                                        <Typography variant="caption" sx={{
                                                                                            px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                                                            bgcolor: r.flow === 'Audit' ? 'primary.light' : 'success.light',
                                                                                            color: r.flow === 'Audit' ? 'primary.dark' : 'success.dark',
                                                                                        }}>{r.flow || 'Audit'}</Typography>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Stack direction="row" spacing={1}>
                                                                                            <Button
                                                                                                size="small"
                                                                                                variant="contained"
                                                                                                color="secondary"
                                                                                                sx={{
                                                                                                    borderRadius: theme.shape.borderRadius,
                                                                                                    textTransform: 'none',
                                                                                                    fontWeight: 600,
                                                                                                    boxShadow: theme.shadows[1],
                                                                                                    '&:hover': {boxShadow: theme.shadows[3]}
                                                                                                }}
                                                                                                onClick={() => {
                                                                                                    setSelectedRow(r);
                                                                                                    setShowAdjustment(true);
                                                                                                }}
                                                                                            >
                                                                                                Audit
                                                                                            </Button>
                                                                                        </Stack>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        });
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={total}
                            page={Math.max(0, Math.min(Math.ceil(total / pageSize) - 1, pageNum - 1))}
                            onPageChange={(_, newPage) => setPageNum(newPage + 1)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(e) => {
                                const next = parseInt(e.target.value, 10) || 25;
                                setPageSize(next);
                                setPageNum(1);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            sx={{mt: 1, flexShrink: 0}}
                        />
                    </Box>
                </Slide>

                {/* Adjustment View */}
                <Slide direction="right" in={showAdjustment} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pl: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        minHeight: 0,
                    }}>
                        {selectedRow ? (
                            <FsAdjustment
                                companyId={selectedRow.companyId}
                                annualId={selectedRow.annualId}
                                companyName={selectedRow.companyName}
                                yearOfAssessment={selectedRow.yearOfAssessment}
                                workflow={selectedRow.flow === 'Account + Audit' ? 'account' : 'audit'}
                                onRequestListRefresh={load}
                                onBack={() => {
                                    setSelectedRow(null);
                                    setShowAdjustment(false);
                                }}
                            />
                        ) : (
                            <Box sx={{p: 2, color: 'text.secondary'}}>Select a row to adjust.</Box>
                        )}
                    </Box>
                </Slide>

                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({...s, open: false}))}
                />
            </Box>
        </Box>
    );
}
